/**
 * @module RecommendationsLoader
 * @description Shared deferred loader for product recommendation sections.
 * Fetches section HTML through Shopify's recommendations endpoint and patches
 * the current custom element in place once recommendations are available.
 * @requires @fc/component
 * @requires @fc/sections
 * @requires @fc/section-response
 * @customElement fc-recommendations
 */
import { FCElement } from '@fc/component';
import { sectionManager } from '@fc/sections';
import { findHTMLNode, isAbortError, LatestRequestController, patchHTML } from '@fc/section-response';

const MARKET_CONTEXT_QUERY_KEYS = ['country', 'locale'];
const RECOMMENDATION_ITEM_SELECTOR = '.resource-list__item, [data-product-id], product-card, product-tile';
const DIALOG_OPEN_EVENT = 'dialog:open';
const SECTION_STYLESHEET_SELECTOR = 'style[data-section-stylesheet]';
const PRODUCT_RECOMMENDATIONS_STYLESHEET_ATTRIBUTE = 'data-fit-product-recommendations-stylesheet';

class RecommendationsLoader extends FCElement {
  static get observedAttributes() {
    return [
      'data-fetch-state',
      'data-load-after-paint',
      'data-load-immediate',
      'data-load-on-idle',
      'data-load-on-dialog-open',
      'data-load-delay',
      'data-preserve-during-refresh',
      'data-recommendations-performed',
      'data-section-id',
      'data-url',
    ];
  }

  #observer = null;
  #idleTimer = 0;
  #idleCallback = 0;
  #idleWarmQueued = false;
  #paintTimer = 0;
  #paintFrame = 0;
  #paintWarmQueued = false;
  #loadQueued = false;
  #request = new LatestRequestController();
  #activeRequestUrl = '';

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(document, DIALOG_OPEN_EVENT, this.#handleDialogOpen);
    this.#queueLoad();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#queueLoad();
  }

  disconnectedCallback() {
    this.#disconnectObserver();
    this.#cancelIdleWarm();
    this.#cancelPaintWarm();
    this.#request.abort();
    super.disconnectedCallback();
  }

  onMorphSync() {
    super.onMorphSync();
    this.#queueLoad();
  }

  warm() {
    this.#startLoad({ force: true });
  }

  beforeMorphSync(next) {
    if (!(next instanceof HTMLElement)) return;
    if (!this.#shouldPreserveNextShell(next)) return;

    this.#disconnectObserver();
    this.#cancelIdleWarm();
    this.#cancelPaintWarm();
    this.#request.abort();
    this.#activeRequestUrl = '';

    next.dataset.fetchState = 'ready';
    next.dataset.hasRecommendations = 'true';
    next.dataset.recommendationsPerformed = 'true';
    next.setAttribute('data-skip-subtree-update', 'true');
    this.setAttribute('data-skip-subtree-update', 'true');
    next.setAttribute('aria-busy', 'false');
    next.removeAttribute('inert');
    next.removeAttribute('hidden');
  }

  #queueLoad() {
    if (this.#loadQueued) return;

    this.#loadQueued = true;
    queueMicrotask(() => {
      this.#loadQueued = false;
      this.#startLoad();
    });
  }

  #startLoad({ force = false } = {}) {
    if (this.dataset.recommendationsPerformed === 'true') return;
    if (!this.dataset.url || !this.dataset.sectionId) return;

    const state = this.dataset.fetchState || 'idle';
    const requestUrl = this.#requestUrl();
    if (state !== 'idle') {
      const canSupersedeActiveLoad = state === 'loading' && this.#activeRequestUrl && requestUrl !== this.#activeRequestUrl;
      if (!canSupersedeActiveLoad) return;
    }

    if (force) {
      void this.#load(requestUrl);
      return;
    }

    if (this.dataset.loadOnDialogOpen === 'true') {
      if (!this.#isInOpenDialog()) return;

      void this.#load(requestUrl);
      return;
    }

    if (this.dataset.loadImmediate === 'true') {
      void this.#load(requestUrl);
      return;
    }

    if (this.dataset.loadAfterPaint === 'true') {
      this.#schedulePaintWarm();
    } else if (this.dataset.loadOnIdle === 'true') {
      this.#scheduleIdleWarm();
    } else {
      this.#cancelIdleWarm();
      this.#cancelPaintWarm();
    }

    if (typeof IntersectionObserver === 'undefined') {
      void this.#load(requestUrl);
      return;
    }

    this.#observer ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#disconnectObserver();
          void this.#load();
          break;
        }
      },
      { rootMargin: '400px 0px' },
    );

    this.#observer.observe(this);
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #scheduleIdleWarm() {
    if (this.#idleWarmQueued) return;

    this.#idleWarmQueued = true;
    const delay = this.#loadDelay();

    this.#idleTimer = window.setTimeout(() => {
      this.#idleTimer = 0;

      const load = () => {
        this.#idleCallback = 0;
        this.#idleWarmQueued = false;
        if (!this.isConnected || this.dataset.recommendationsPerformed === 'true') return;

        this.#startLoad({ force: true });
      };

      if (typeof window.requestIdleCallback === 'function') {
        this.#idleCallback = window.requestIdleCallback(load, { timeout: 1500 });
        return;
      }

      load();
    }, delay);
  }

  #schedulePaintWarm() {
    if (this.#paintWarmQueued) return;

    this.#paintWarmQueued = true;
    const delay = this.#loadDelay(0);

    this.#paintTimer = window.setTimeout(() => {
      this.#paintTimer = 0;

      const load = () => {
        this.#paintFrame = 0;
        this.#paintWarmQueued = false;
        if (!this.isConnected || this.dataset.recommendationsPerformed === 'true') return;

        this.#startLoad({ force: true });
      };

      if (typeof window.requestAnimationFrame !== 'function') {
        load();
        return;
      }

      this.#paintFrame = window.requestAnimationFrame(() => {
        this.#paintFrame = window.requestAnimationFrame(load);
      });
    }, delay);
  }

  #cancelIdleWarm() {
    if (this.#idleTimer) {
      window.clearTimeout(this.#idleTimer);
      this.#idleTimer = 0;
    }

    if (this.#idleCallback && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(this.#idleCallback);
      this.#idleCallback = 0;
    }

    this.#idleWarmQueued = false;
  }

  #cancelPaintWarm() {
    if (this.#paintTimer) {
      window.clearTimeout(this.#paintTimer);
      this.#paintTimer = 0;
    }

    if (this.#paintFrame && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(this.#paintFrame);
      this.#paintFrame = 0;
    }

    this.#paintWarmQueued = false;
  }

  #loadDelay(defaultDelay = 1200) {
    const delay = Number.parseInt(this.dataset.loadDelay || '', 10);
    if (!Number.isFinite(delay)) return defaultDelay;

    return Math.min(Math.max(delay, 0), 10000);
  }

  #handleDialogOpen = (event) => {
    if (this.dataset.loadOnDialogOpen !== 'true') return;

    const dialog = event?.detail?.dialog;
    if (!(dialog instanceof HTMLElement) || !dialog.contains(this)) return;

    if (this.dataset.fetchState === 'ready' || this.dataset.recommendationsPerformed === 'true') {
      this.#refreshPatchedCarousels();
      return;
    }

    this.#queueLoad();
  };

  #isInOpenDialog() {
    const dialog = this.closest('dialog');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  async #load(requestUrl = this.#requestUrl()) {
    this.#cancelIdleWarm();
    this.#cancelPaintWarm();
    const request = this.#request.start();
    this.#activeRequestUrl = requestUrl;
    this.dataset.fetchState = 'loading';
    this.setAttribute('aria-busy', 'true');
    this.#hideWhileLoading();
    this.#disconnectObserver();

    try {
      const doc = await sectionManager.getHTMLDocument(requestUrl, {
        cache: false,
        signal: request.signal,
      });
      if (!request.isCurrent()) return;

      const next = findHTMLNode(doc, `[data-section-id="${this.dataset.sectionId}"]`);
      if (!(next instanceof HTMLElement)) {
        this.#hideUnavailable('empty');
        return;
      }

      const hasRecommendations = next.dataset.hasRecommendations === 'true' && this.#hasRecommendationItems(next);
      const nextState = hasRecommendations ? next.dataset.fetchState || 'ready' : 'empty';
      if (!hasRecommendations) {
        this.#hideUnavailable(nextState);
        return;
      }

      const patchMetrics = this.#capturePatchMetrics();
      this.#prepareNextForHiddenPatch(next);
      this.#prepareNextForReveal(next);
      this.#attachProductRecommendationsStylesheet(doc, next);
      patchHTML(this, doc, next, { childrenOnly: false });
      this.#stabilizeScrollAfterPatch(patchMetrics);
      this.dataset.fetchState = nextState;
      this.dataset.hasRecommendations = 'true';
      this.dataset.recommendationsPerformed = 'true';
      this.setAttribute('aria-busy', 'false');
      this.removeAttribute('hidden');
      this.removeAttribute('inert');
      this.#revealPatchedContent();
      this.#refreshPatchedCarousels();
    } catch (error) {
      if (isAbortError(error)) return;
      if (request.isCurrent()) this.#hideUnavailable('failed');
    } finally {
      if (this.#activeRequestUrl === requestUrl) {
        this.#activeRequestUrl = '';
      }
      request.finish();
    }
  }

  #requestUrl() {
    const url = new URL(this.dataset.url, window.location.origin);
    const current = new URL(window.location.href);

    for (const key of MARKET_CONTEXT_QUERY_KEYS) {
      const value = current.searchParams.get(key);
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }

    return url.href;
  }

  #hideUnavailable(state) {
    const patchMetrics = this.#capturePatchMetrics();
    this.dataset.fetchState = state;
    this.dataset.hasRecommendations = 'false';
    this.setAttribute('aria-busy', 'false');
    this.setAttribute('hidden', '');
    this.removeAttribute('inert');
    delete this.dataset.revealState;
    this.#stabilizeScrollAfterPatch(patchMetrics);
  }

  #hideWhileLoading() {
    if (this.dataset.hideUntilReady !== 'true') return;

    this.setAttribute('hidden', '');
  }

  #prepareNextForHiddenPatch(next) {
    if (this.dataset.hideUntilReady !== 'true') return;

    next.dataset.fetchState = 'loading';
    next.setAttribute('aria-busy', 'true');
    next.setAttribute('hidden', '');
  }

  #prepareNextForReveal(next) {
    if (this.dataset.revealContent !== 'true') return;

    next.dataset.revealState = 'pending';
  }

  #attachProductRecommendationsStylesheet(doc, next) {
    if (!this.classList.contains('product-recommendations')) return;
    if (next.querySelector(`[${PRODUCT_RECOMMENDATIONS_STYLESHEET_ATTRIBUTE}]`)) return;

    const stylesheet = doc.querySelector(SECTION_STYLESHEET_SELECTOR);
    if (!(stylesheet instanceof HTMLStyleElement)) return;

    const importedStylesheet = document.importNode(stylesheet, true);
    importedStylesheet.setAttribute(PRODUCT_RECOMMENDATIONS_STYLESHEET_ATTRIBUTE, '');
    next.prepend(importedStylesheet);
  }

  #revealPatchedContent() {
    if (this.dataset.revealContent !== 'true') return;

    window.requestAnimationFrame(() => {
      if (!this.isConnected || this.dataset.fetchState !== 'ready') return;

      this.dataset.revealState = 'visible';
    });
  }

  #capturePatchMetrics() {
    if (this.dataset.stabilizeScroll !== 'true') return null;

    const rect = this.getBoundingClientRect();
    return {
      bottom: rect.bottom,
      height: rect.height,
    };
  }

  #stabilizeScrollAfterPatch(metrics) {
    if (!metrics || metrics.bottom > 0) return;

    const delta = this.getBoundingClientRect().height - metrics.height;
    if (Math.abs(delta) < 1) return;

    window.scrollBy(0, delta);
  }

  #refreshPatchedCarousels() {
    const carousels = this.querySelectorAll('slide-carousel');
    if (!carousels.length) return;

    const refresh = () => {
      for (const carousel of carousels) {
        if (typeof carousel.refresh !== 'function') continue;
        carousel.refresh();
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(refresh));
    setTimeout(refresh, 250);
  }

  #shouldPreserveNextShell(next) {
    if (this.dataset.preserveDuringRefresh !== 'true' && next.dataset.preserveDuringRefresh !== 'true') return false;

    const preserveKey = this.dataset.preserveKey || '';
    if (!preserveKey || preserveKey !== next.dataset.preserveKey) return false;

    if (next.dataset.hideUntilReady !== 'true') return false;
    if (this.hasAttribute('hidden')) return false;
    if (next.dataset.recommendationsPerformed === 'true') return false;
    if (this.dataset.hasRecommendations !== 'true') return false;
    if (!this.#hasRecommendationItems(this)) return false;
    if (this.#hasRecommendationItems(next)) return false;

    const nextState = next.dataset.fetchState || 'idle';
    return nextState === 'idle' || nextState === 'loading';
  }
  #hasRecommendationItems(root) {
    return Boolean(root.querySelector(RECOMMENDATION_ITEM_SELECTOR));
  }
}

if (!customElements.get('fc-recommendations')) {
  customElements.define('fc-recommendations', RecommendationsLoader);
}
